import { Components, Theme } from '@mui/material/styles';

export const muiTypographyOverride: Components<Theme>['MuiTypography'] = {
  styleOverrides: {
    root: {
      fontFamily: 'inherit',
    },
    h1: ({ theme }) => ({
      fontWeight: 400,
      fontSize: '40px',
      lineHeight: 1.2,
      letterSpacing: '-0.5px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '48px',
        letterSpacing: '-0.75px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '60px',
        letterSpacing: '-1.5px',
      },
      [theme.breakpoints.up('lg')]: {
        fontSize: '76px',
        letterSpacing: '-1.5px',
      },
    }),
    h2: ({ theme }) => ({
      fontWeight: 400,
      fontSize: '32px',
      lineHeight: 1.2,
      letterSpacing: '-0.25px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '40px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '48px',
        letterSpacing: '-0.5px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '60px',
        letterSpacing: '-0.5px',
      },
    }),
    h3: ({ theme }) => ({
      fontWeight: 400,
      fontSize: '24px',
      lineHeight: 1.2,
      [theme.breakpoints.up('sm')]: {
        fontSize: '28px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '34px',
        letterSpacing: '0.25px',
      },
    }),
    h4: ({ theme }) => ({
      fontWeight: 400,
      fontSize: '20px',
      lineHeight: 1.2,
      [theme.breakpoints.up('sm')]: {
        fontSize: '24px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '28px',
      },
    }),
    h5: ({ theme }) => ({
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '18px',
      },
      [theme.breakpoints.up('md')]: {
        fontSize: '20px',
      },
    }),
    h6: ({ theme }) => ({
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '16px',
      },
    }),
    body1: ({ theme }) => ({
      fontSize: '14px',
      lineHeight: 1.5,
      letterSpacing: '0.15px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '16px',
        letterSpacing: '0.5px',
      },
    }),
    body2: ({ theme }) => ({
      fontSize: '12px',
      lineHeight: 1.5,
      letterSpacing: '0.25px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '14px',
      },
    }),
    caption: ({ theme }) => ({
      fontSize: '10px',
      lineHeight: 1.5,
      letterSpacing: '0.4px',
      [theme.breakpoints.up('sm')]: {
        fontSize: '12px',
      },
    }),
    overline: ({ theme }) => ({
      fontWeight: 400,
      letterSpacing: '4px',
      [theme.breakpoints.up('md')]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
      [theme.breakpoints.down('md')]: {
        fontSize: '12px',
        lineHeight: '18px',
      },
    }),
  },
  variants: [
    {
      props: { variant: 'displayLG' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '76px',
          lineHeight: '90px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '48px',
          lineHeight: '56px',
        },
      }),
    },
    {
      props: { variant: 'displayMD' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '60px',
          lineHeight: '72px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '40px',
          lineHeight: '48px',
        },
      }),
    },
    {
      props: { variant: 'displaySM' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '48px',
          lineHeight: '56px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '32px',
          lineHeight: '40px',
        },
      }),
    },
    {
      props: { variant: 'headlineLGBold' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '40px',
          lineHeight: '48px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '28px',
          lineHeight: '38px',
        },
      }),
    },
    {
      props: { variant: 'headlineLGMedium' },
      style: ({ theme }) => ({
        fontWeight: 500,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '40px',
          lineHeight: '48px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '28px',
          lineHeight: '38px',
        },
      }),
    },
    {
      props: { variant: 'headlineLGItalic' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        fontStyle: 'italic',
        [theme.breakpoints.up('md')]: {
          fontSize: '40px',
          lineHeight: '48px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '28px',
          lineHeight: '38px',
        },
      }),
    },
    {
      props: { variant: 'headlineMDBold' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '32px',
          lineHeight: '42px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '24px',
          lineHeight: '36px',
        },
      }),
    },
    {
      props: { variant: 'headlineMDMedium' },
      style: ({ theme }) => ({
        fontWeight: 500,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '32px',
          lineHeight: '42px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '24px',
          lineHeight: '36px',
        },
      }),
    },
    {
      props: { variant: 'headlineSMBold' },
      style: ({ theme }) => ({
        fontWeight: 700,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '24px',
          lineHeight: '32px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '20px',
          lineHeight: '30px',
        },
      }),
    },
    {
      props: { variant: 'headlineSMMedium' },
      style: ({ theme }) => ({
        fontWeight: 500,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '24px',
          lineHeight: '32px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '20px',
          lineHeight: '30px',
        },
      }),
    },
    {
      props: { variant: 'subtitleLG' },
      style: ({ theme }) => ({
        fontWeight: 500,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '20px',
          lineHeight: '30px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '18px',
          lineHeight: '26px',
        },
      }),
    },
    {
      props: { variant: 'subtitleMD' },
      style: ({ theme }) => ({
        fontWeight: 500,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '18px',
          lineHeight: '24px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '16px',
          lineHeight: '24px',
        },
      }),
    },
    {
      props: { variant: 'bodyLGRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '18px',
          lineHeight: '26px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '16px',
          lineHeight: '24px',
        },
      }),
    },
    {
      props: { variant: 'bodyLGSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '18px',
          lineHeight: '26px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '16px',
          lineHeight: '24px',
        },
      }),
    },
    {
      props: { variant: 'bodyMDRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '16px',
          lineHeight: '24px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'bodyMDSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '16px',
          lineHeight: '24px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'bodySMRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'bodySMSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'captionLGRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'captionLGSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '14px',
          lineHeight: '20px',
        },
      }),
    },
    {
      props: { variant: 'captionMDRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '12px',
          lineHeight: '14px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '12px',
          lineHeight: '14px',
        },
      }),
    },
    {
      props: { variant: 'captionMDSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '12px',
          lineHeight: '14px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '12px',
          lineHeight: '14px',
        },
      }),
    },
    {
      props: { variant: 'captionSMRegular' },
      style: ({ theme }) => ({
        fontWeight: 400,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '10px',
          lineHeight: '12px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '10px',
          lineHeight: '12px',
        },
      }),
    },
    {
      props: { variant: 'captionSMSemibold' },
      style: ({ theme }) => ({
        fontWeight: 600,
        letterSpacing: 'auto',
        [theme.breakpoints.up('md')]: {
          fontSize: '10px',
          lineHeight: '12px',
        },
        [theme.breakpoints.down('md')]: {
          fontSize: '10px',
          lineHeight: '12px',
        },
      }),
    },
  ],
};
